import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { CONNECTORS } from '../connectors'
import { ROUTES } from '../routes'
import Button from './Button'
import IntegrationEventList from './IntegrationEventList'
import Select from './Select'
import { InlineAlert, PageHeader } from './ui'
import { periodLabel } from './historyExportFormat'
import '../styles/integration-logs.css'

const PAGE_SIZE = 20

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'success', label: 'Успешно' },
  { value: 'warning', label: 'Предупреждение' },
  { value: 'error', label: 'Ошибка' },
  { value: 'running', label: 'Выполняется' },
  { value: 'skipped', label: 'Пропущено' }
]

export default function IntegrationLogs () {
  const { slug } = useParams()
  // Из итога выгрузки прошлых периодов журнал открывается отобранным по ней.
  const [searchParams] = useSearchParams()
  const requestId = useRef(0)
  const [items, setItems] = useState([])
  // Плашка ошибок выгрузки открывает журнал сразу с отбором по статусу.
  const [status, setStatus] = useState(() => STATUS_OPTIONS.some(option => option.value === searchParams.get('status')) ? searchParams.get('status') : '')
  // Один список «Сценарий»: сценарии и выгрузки прошлых периодов (приёмка
  // 2026-10-14). Значение выгрузки — `export:<id>`.
  const [source, setSource] = useState(() => searchParams.get('export_id') ? `export:${searchParams.get('export_id')}` : '')
  const scenarioId = source && !source.startsWith('export:') ? source : ''
  const exportId = source.startsWith('export:') ? source.slice('export:'.length) : ''
  const [scenarios, setScenarios] = useState([])
  const [exportsList, setExportsList] = useState([])
  const [page, setPage] = useState({ has_more: false, next_cursor: null })
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [pageError, setPageError] = useState(null)
  const [scenarioListFailed, setScenarioListFailed] = useState(false)
  const title = CONNECTORS[slug]?.title || slug

  useEffect(() => {
    let active = true
    api.getConnectorHub(slug)
      .then(result => {
        if (!active) return
        setScenarios(result.scenarios || [])
        setScenarioListFailed(false)
      })
      // Пустой фильтр по сценариям нельзя отличить от «сценариев нет».
      .catch(() => {
        if (!active) return
        setScenarios([])
        setScenarioListFailed(true)
      })
    // Выгрузки есть не у всех источников: отказ — просто без них в списке.
    api.listExports(slug)
      .then(result => { if (active) setExportsList(result?.exports || []) })
      .catch(() => { if (active) setExportsList([]) })
    return () => { active = false }
  }, [slug])

  const loadEvents = useCallback((before = null, append = false) => {
    const currentRequest = ++requestId.current
    if (append) setLoadingMore(true)
    else {
      setLoading(true)
      setItems([])
    }
    if (append) setPageError(null)
    else {
      setError(null)
      setPageError(null)
    }
    const params = { connector: slug, limit: PAGE_SIZE }
    if (status) params.status = status
    if (scenarioId) params.scenario_id = scenarioId
    if (exportId) params.export_id = exportId
    if (before) params.before = before

    api.getIntegrationEvents(params)
      .then(result => {
        if (currentRequest !== requestId.current) return
        setItems(current => append ? [...current, ...(result.items || [])] : (result.items || []))
        setPage(result.page || { has_more: false, next_cursor: null })
      })
      .catch(err => {
        if (currentRequest !== requestId.current) return
        if (append) setPageError(err.message)
        else setError(err.message)
      })
      .finally(() => {
        if (currentRequest !== requestId.current) return
        setLoading(false)
        setLoadingMore(false)
      })
  }, [exportId, scenarioId, slug, status])

  useEffect(() => {
    loadEvents()
    return () => { requestId.current += 1 }
  }, [loadEvents])

  return (
    <div className='integration-log-page'>
      <Link className='integration-log-page__back' to={ROUTES.INTEGRATION(slug)}>← {title}</Link>
      <PageHeader title={`Лог событий — ${title}`} description='Результаты синхронизации без технических данных и секретов.' />

      <div className='integration-log-page__filters' role='group' aria-label='Фильтры журнала'>
        <label>
          <span>Статус</span>
          <Select aria-label='Статус' value={status} onChange={event => setStatus(event.target.value)}>
            {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </label>
        <label>
          <span>Сценарий</span>
          <Select aria-label='Сценарий' value={source} onChange={event => setSource(event.target.value)}>
            <option value=''>Все сценарии и выгрузки</option>
            {scenarios.map(scenario => <option key={scenario.id} value={scenario.id}>{scenario.name}</option>)}
            {exportsList.map(item => (
              <option key={`export:${item.id}`} value={`export:${item.id}`}>Выгрузка {periodLabel(item.period_from, item.period_to)}</option>
            ))}
            {/* Выгрузка из адреса, которой нет в списке, — подписана номером. */}
            {exportId && !exportsList.some(item => String(item.id) === exportId) && (
              <option value={source}>Выгрузка №{exportId}</option>
            )}
          </Select>
        </label>
      </div>

      {scenarioListFailed && (
        <InlineAlert title='Фильтр по сценариям недоступен' tone='warning'>
          <span>Не удалось загрузить список сценариев. События показаны полностью, фильтр останется пустым до перезагрузки страницы.</span>
        </InlineAlert>
      )}

      <IntegrationEventList error={error} items={items} loading={loading} onRetry={() => loadEvents()} />
      {pageError && (
        <InlineAlert title='Не удалось загрузить следующую страницу' tone='error'>
          <span>{pageError}</span>
          <Button className='integration-log-page__retry' onClick={() => loadEvents(page.next_cursor, true)} variant='ghost'>Повторить загрузку</Button>
        </InlineAlert>
      )}
      {page.has_more && !error && !pageError && (
        <div className='integration-log-page__more'>
          <Button loading={loadingMore} disabled={loadingMore} onClick={() => loadEvents(page.next_cursor, true)} variant='stroke'>Показать ещё</Button>
        </div>
      )}
    </div>
  )
}
